import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { Add, Search, Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiService } from "../api/services/apiService";
import { useDocuments } from "../hooks/useDocuments";
import { PaginationControls } from "./common/PaginationControls";
import type { Document, EntityStatus } from "../types";

const ITEMS_PER_PAGE = 10;

const ManageDocuments: React.FC = () => {
  const navigate = useNavigate();
  const { documents: allDocuments, loading, error } = useDocuments();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // delete
  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // edit
  const [editDocument, setEditDocument] = useState<Document | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDocuments(allDocuments);
  }, [allDocuments]);

  /* ---------------- Delete ---------------- */

  const handleDeleteClick = (uuid: string) => {
    setDeleteUuid(uuid);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUuid) return;
    setIsDeleting(true);

    try {
      const success = await apiService.deleteDocument(deleteUuid);
      if (success) {
        setDocuments((prev) => prev.filter((d) => d.uuid !== deleteUuid));
      }
    } catch (err: any) {
      alert(err.message || "Failed to delete document");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
      setDeleteUuid(null);
    }
  };

  /* ---------------- Edit ---------------- */

  const handleSaveEdit = async () => {
    if (!editDocument) return;

    setIsSaving(true);
    try {
      const updated = await apiService.updateDocument(editDocument.uuid, {
        name: editDocument.name,
        description: editDocument.description ?? undefined,
        status: editDocument.status,
      });

      setDocuments((prev) =>
        prev.map((d) => (d.uuid === updated.uuid ? updated : d)),
      );
      setEditDocument(null);
    } catch (err: any) {
      alert(err.message || "Failed to update document");
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------------- Filtering & Pagination ---------------- */

  const filteredDocuments = documents.filter((doc) =>
    doc.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE);
  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Documents
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/manage-assets/documents/add")}
          >
            Add Document
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !error && (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedDocuments.map((doc) => (
                  <TableRow key={doc.uuid} hover>
                    <TableCell>{doc.name}</TableCell>
                    <TableCell>{doc.description}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={doc.status}
                        color={doc.status === "ACTIVE" ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => setEditDocument(doc)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(doc.uuid)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={!!editDocument}
        onClose={() => setEditDocument(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Tenant ID"
            margin="normal"
            value={editDocument?.tenantId || ""}
            disabled
          />

          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={editDocument?.name || ""}
            onChange={(e) =>
              setEditDocument((prev) =>
                prev ? { ...prev, name: e.target.value } : prev,
              )
            }
          />

          <TextField
            fullWidth
            label="Description"
            margin="normal"
            value={editDocument?.description || ""}
            onChange={(e) =>
              setEditDocument((prev) =>
                prev ? { ...prev, description: e.target.value } : prev,
              )
            }
          />

          <TextField
            select
            fullWidth
            label="Status"
            margin="normal"
            value={editDocument?.status || "INACTIVE"}
            onChange={(e) =>
              setEditDocument((prev) =>
                prev
                  ? { ...prev, status: e.target.value as EntityStatus }
                  : prev,
              )
            }
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDocument(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={isSaving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <strong>Delete Document?</strong>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete this document?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={isDeleting}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageDocuments;
